import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import "../styles/UserListeAdminPage.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { User } from "../types/User";
import { useNavigate } from "react-router-dom"; 
import { useLocation } from "react-router-dom";
import { userStatus } from "../constants/UserStatus";
import { roleService } from "../services/RoleService";
import { useAuth } from "../context/AuthContext";
import { Role } from "../types/Role";
import { userService } from "../services/UserService";
import AuthentifiedPage from "./AuthentifiedPage";

const StatusBadge: React.FC<{ statut: User["statut"] }> = ({ statut }) => {
  return (
    <span className={`user-btn-status ${statut}`}>
      <span className="status-indicator"></span>
      {statut === "actif" ? "Actif" : "Inactif"}
    </span>
  );
};

const UserListeAdminPage: React.FC = () => {
  const { api } = useAuth();
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [roles, setRoles] = useState<Role[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const navigate = useNavigate(); 
  const [query, setQuery] = useState("");
  const [statutFilter, setStatutFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Chargement des données
  useEffect(() => {
    const fetchRolesAndUsers = async () => {
      try {
        const rolesData = await roleService.getAllRoles(api);
        setRoles(rolesData);
        const userData = await userService.getAllUsers(api);
        setUsers(userData);
      } catch (error) {
        console.error('Erreur lors du chargement des rôles:', error);
      }
    };
    fetchRolesAndUsers();
  }, [api]);

  // Statistiques dynamiques des rôles
  const stats = useMemo(() => {
    const roleCounts: { [role: string]: number } = {};
    users.forEach(user => {
      if (roleCounts[user.role]) {
        roleCounts[user.role]++;
      } else {
        roleCounts[user.role] = 1;
      }
    });
    return {
      totalUsers: users.length,
      roleCounts
    };
  }, [users]);

  const filtered = useMemo(() => {
    let list = users.slice();
    if (query) {
      const q = query.toLowerCase();
      list = list.filter(
        (u) =>
          u.nom.toLowerCase().includes(q) ||
          u?.telephone?.toLowerCase().includes(q) ||
          u.username.toLowerCase().includes(q)
      );
    }
    if (statutFilter) list = list.filter((u) => u.statut === statutFilter);
    if (roleFilter) list = list.filter((u) => u.role === roleFilter);
    return list;
  }, [users, query, statutFilter, roleFilter]);

  const handleArchiveUser = async (id: number) => {
    try {
      await userService.archiveUser(api, id);
      alert("Utilisateur archivé avec succès.");
      const userData = await userService.getAllUsers(api);
      setUsers(userData);
    } catch (error: any) {
      alert(error.message || "Erreur lors de l'archivage.");
    }
  };

  // Pagination
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageData = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleAddUser = () => {
    navigate("/admin/create-user"); 
  };

  return (
    <AuthentifiedPage>
      <main className="container py-4">
        {/* Header avec compteur et bouton alignés */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <h4 className="user-admin-title mb-0">Liste des utilisateurs</h4>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="user-admin-counts d-flex flex-wrap gap-3">
              <span className="total">
                Nombre d'utilisateurs : <strong>{stats.totalUsers}</strong>
              </span>
              {Object.entries(stats.roleCounts).map(([role, count]) => (
                <span key={role} className="role-badge">
                  {role} : <strong>{count}</strong>
                </span>
              ))}
            </div>
            <div className="button-group">
              <button className="btn-add-user" onClick={handleAddUser}>
                <i className="bi bi-plus-lg"></i> Ajouter un utilisateur
              </button>
            </div>
          </div>
        </div>

        {/* Barre de filtres */}
        <div className="filters-bar">
          <div className="input-wrapper">
            <span className="search-icon">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher nom / téléphone / username"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <select
            className="custom-select"
            value={statutFilter}
            onChange={(e) => setStatutFilter(e.target.value)}
          >
            <option value="">Tous les statuts</option>
            {userStatus.map((status) => (
              <option key={status.value.toString()} value={status.label.toLowerCase()}>
                {status.label}
              </option>
            ))}
          </select>

          <select
            className="custom-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="">Tous les rôles</option>
            {roles.map(role => (
              <option key={role.id} value={role.label}>
                {role.label}
              </option>
            ))}
          </select>
        </div>

        {/* Tableau */}
        <div className="table-responsive shadow-sm">
          <table className="table table-hover align-middle">
            <thead className="table-success">
              <tr>
                <th>ID</th>
                <th>Nom</th>
                <th>Téléphone</th>
                <th>Rôle</th>
                <th>Date création</th>
                <th>Adresse</th>
                <th>Statut du compte</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pageData.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>
                    <div className="user-name">{user.nom}</div>
                    <div className="user-email">{user.email}</div>
                  </td>
                  <td>{user.telephone}</td>
                  <td>
                    <span className={`user-role-badge ${user.role}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    {user.createdAt 
                      ? new Date(user.createdAt).toLocaleString("fr-FR") 
                      : "—"}
                  </td>
                  <td>{user.adresse}</td>
                  <td><StatusBadge statut={user.statut} /></td>
                  <td className="text-center">
                    <button
                      className="btn btn-sm btn-outline-success me-2"
                      title="Modifier l'utilisateur"
                      onClick={() => navigate(`/admin/update-user/${user.id}`)}
                    >
                      <i className="bi bi-pencil-square"></i>
                    </button>

                    <button
                      className="btn btn-sm btn-outline-danger"
                      title="Archiver l'utilisateur"
                      onClick={() => {
                        if (window.confirm("Voulez-vous vraiment archiver cet utilisateur ?")) {
                          handleArchiveUser(user.id);
                        }
                      }}
                    >
                      <i className="bi bi-trash-fill"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
          <small className="text-muted">
            {pageData.length} utilisateurs affichés sur {total}
          </small>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(page - 1)}>
                  <i className="bi bi-chevron-left"></i>
                </button>
              </li>
              {pageNumbers.map((num) => (
                <li
                  key={num}
                  className={`page-item ${num === currentPage ? "active" : ""}`}
                >
                  <button className="page-link" onClick={() => setPage(num)}>
                    {num}
                  </button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(page + 1)}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </main>
    </AuthentifiedPage>
  );
};

export default UserListeAdminPage;